/**
 * Managed social posting (Launch L1): connect your own accounts through Auteur's
 * managed Postiz. The browser never sees a Postiz key; the server binds each
 * connected account to this organization only.
 */
import { requestJson, responseArray, responseRecord } from './http';

export interface SocialChannel {
  channel_id: string;
  platform: string;
  display_name: string;
  profile?: string;
  connected: boolean;
  bound_at: string;
}

export interface SocialPlatform {
  id: string;
  label: string;
  status: 'available' | 'review_pending' | 'not_available' | string;
  note: string;
  connectable: boolean;
}

export interface SocialConnections {
  channels: SocialChannel[];
  platforms: SocialPlatform[];
  relayReachable: boolean;
}

export type ClaimState = 'bound' | 'pending' | 'expired' | 'needs_operator';

function str(record: Record<string, unknown>, field: string): string {
  return typeof record[field] === 'string' ? (record[field] as string) : '';
}

export async function fetchSocialConnections(): Promise<SocialConnections> {
  const payload = await requestJson('/v1/social/connections');
  const channels = responseArray(payload, 'channels').map((raw) => {
    const row = responseRecord(raw);
    return {
      channel_id: str(row, 'channel_id'), platform: str(row, 'platform'), display_name: str(row, 'display_name'),
      profile: str(row, 'profile'), connected: row.connected === true, bound_at: str(row, 'bound_at'),
    };
  });
  const platforms = responseArray(payload, 'platforms').map((raw) => {
    const row = responseRecord(raw);
    return { id: str(row, 'id'), label: str(row, 'label'), status: str(row, 'status'), note: str(row, 'note'),
      connectable: row.connectable === true };
  });
  return { channels, platforms, relayReachable: (payload as Record<string, unknown>).relay_reachable !== false };
}

export async function startSocialConnect(platform: string): Promise<{ inviteId: string; url: string }> {
  const payload = responseRecord(await requestJson(`/v1/social/connect/${encodeURIComponent(platform)}`, {
    method: 'POST', body: '{}',
  }));
  const url = str(payload, 'url');
  if (!url.startsWith('https://')) throw new Error('the connect link is not https');
  return { inviteId: str(payload, 'invite_id'), url };
}

export async function claimSocialConnect(inviteId: string): Promise<{ state: ClaimState; channel?: SocialChannel }> {
  const payload = responseRecord(await requestJson('/v1/social/claim', {
    method: 'POST', body: JSON.stringify({ invite_id: inviteId }),
  }));
  const state = str(payload, 'state') as ClaimState;
  return { state: ['bound', 'pending', 'expired', 'needs_operator'].includes(state) ? state : 'pending' };
}

export async function disconnectSocial(channelId: string): Promise<void> {
  await requestJson('/v1/social/disconnect', { method: 'POST', body: JSON.stringify({ channel_id: channelId }) });
}
